import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardNav, type CardNavItem } from '@/components/ui/navigation';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { SECTIONS } from '@/types/sections';


export type EmployeeSection = 'profile' | 'activeContract';

interface EmployeeSectionNavProps {
  /** Collaborator the sections belong to. */
  employeeId: number;
  /** Section currently being displayed. */
  activeSection: EmployeeSection;
}

// Resolved lazily inside the handler: `routes` imports the pages that render this component, so
// reading ROUTE_DEFINITIONS at module scope would hit the circular import before it is initialised.
const sectionPath = (section: EmployeeSection): string | undefined => ({
  profile: ROUTE_DEFINITIONS.EMPLEADOS_PERFIL.path,
  activeContract: ROUTE_DEFINITIONS.EMPLEADOS_CONTRATO_ACTIVO.path,
}[section]);

/**
 * Navigation between the two sections of a collaborator: personal profile and active contract.
 * Renders above the page card, so it is passed through PageLayout's `headerContent`.
 */
export const EmployeeSectionNav: React.FC<EmployeeSectionNavProps> = ({
  employeeId,
  activeSection,
}) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  // Access to the active contract depends on the "Condiciones laborales" section of the
  // collaborators screen, not on the Contracts screen code. Without an explicit screenCode the hook
  // resolves the current screen, which for both sections is the collaborators listing.
  const { canSection, isLoaded: permissionsLoaded } = usePagePermissions();
  const canViewLaborConditions = canSection(SECTIONS.CONDICIONES_LABORALES, 'view');

  const items = useMemo<CardNavItem[]>(() => {
    const sections: CardNavItem[] = [
      {
        id: 'profile',
        label: translate('employee:sections.profile'),
        icon: 'user',
        'data-testid': 'employee-section-nav-profile',
      },
    ];

    if (permissionsLoaded && canViewLaborConditions) {
      sections.push({
        id: 'activeContract',
        label: translate('employee:sections.activeContract'),
        icon: 'fileAlt',
        'data-testid': 'employee-section-nav-active-contract',
      });
    }

    return sections;
  }, [translate, permissionsLoaded, canViewLaborConditions]);

  const handleItemClick = (id: string) => {
    const path = sectionPath(id as EmployeeSection);
    if (!path) return;
    void navigate(path.replace(':employeeId', String(employeeId)));
  };

  return (
    <CardNav
      items={items}
      activeItemId={activeSection}
      onItemClick={handleItemClick}
      ariaLabel={translate('employee:sections.navigationLabel')}
      data-testid="employee-section-nav"
    />
  );
};

export default EmployeeSectionNav;
